import { ChangeEvent, useMemo, useRef, useState } from "react"

import { useGameSearch } from "hooks/useGameSearch"

import { GameInterface } from "services/game/type"

interface SearchGamesPanelProps {
  // When true (MANUAL sections), search results are split into two groups:
  // "in this section" (move) and "not in this section" (add). When false
  // (BUILTIN), only the in-section group is shown — admins can't add or
  // remove games, only reorder.
  allowAddRemove: boolean
  // Current local order of game identifiers in the section. Used to detect
  // membership and compute current position.
  gameIds: string[]
  // Called when admin submits a new position for an in-section game. Parent
  // owns the actual mutation; this component just provides the affordance.
  onMoveGame: (g: GameInterface, newPosition: number) => void
  // Called when admin clicks Add on a not-in-section result (MANUAL only).
  onAddGame: (g: GameInterface) => void
}

export const SearchGamesPanel = ({ allowAddRemove, gameIds, onMoveGame, onAddGame }: SearchGamesPanelProps) => {
  const { query, setQuery, results: searchResults, loading: searching, clear } = useGameSearch()

  const [searchOpen, setSearchOpen] = useState(false)
  // Per-result typed position for the "Move to position N" affordance.
  // Keyed by game identifier; cleared after a successful move.
  const [movePositions, setMovePositions] = useState<Record<string, string>>({})
  // The search input blurs whenever focus moves into the dropdown (e.g. clicking
  // the position input). We check `relatedTarget` against this ref so the
  // dropdown only closes when focus genuinely leaves it.
  const dropdownRef = useRef<HTMLDivElement>(null)

  const gameIdsSet = useMemo(() => new Set(gameIds), [gameIds])
  const inSectionResults = useMemo(() => searchResults.filter((g) => gameIdsSet.has(g.identifier)), [searchResults, gameIdsSet])
  const notInSectionResults = useMemo(() => searchResults.filter((g) => !gameIdsSet.has(g.identifier)), [searchResults, gameIdsSet])

  const resetSearch = () => {
    clear()
    setSearchOpen(false)
    setMovePositions({})
  }

  const submitMove = (g: GameInterface) => {
    const currentPos = gameIds.indexOf(g.identifier) + 1
    const inputValue = movePositions[g.identifier] ?? String(currentPos)
    const n = Number(inputValue)
    if (!Number.isFinite(n)) return
    onMoveGame(g, n)
    resetSearch()
  }

  const submitAdd = (g: GameInterface) => {
    onAddGame(g)
    resetSearch()
  }

  const queryReady = query.trim().length >= 2

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={allowAddRemove ? "Search games to move or add (min 2 chars)…" : "Search games in this section (min 2 chars)…"}
        className="input input-bordered w-full bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
        value={query}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setQuery(e.target.value)
          if (e.target.value.trim().length >= 2) setSearchOpen(true)
        }}
        onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
        onBlur={(e) => {
          // Keep the dropdown open if focus moved into it (position input or
          // a Move/Add button). Only close when focus genuinely leaves it.
          if (dropdownRef.current?.contains(e.relatedTarget as Node)) return
          setTimeout(() => setSearchOpen(false), 200)
        }}
      />
      {searchOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-10 mt-1 left-0 right-0 max-h-96 overflow-auto rounded-lg border border-black/10 dark:border-white/20 bg-white dark:bg-[#0C0C1A] shadow-lg"
        >
          {searching && <div className="px-3 py-2 text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">Searching…</div>}
          {!searching && searchResults.length === 0 && queryReady && (
            <div className="px-3 py-2 text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">No matches.</div>
          )}

          {inSectionResults.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide bg-black/[0.04] dark:bg-white/[0.06] text-[rgba(6,7,21,0.7)] dark:text-white/70">
                In this section — set new position
              </div>
              {inSectionResults.map((g) => {
                const currentPos = gameIds.indexOf(g.identifier) + 1
                const inputValue = movePositions[g.identifier] ?? String(currentPos)
                return (
                  <div
                    key={g.identifier}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-[rgba(132,_0,_168,_0.06)] dark:hover:bg-[rgba(132,_0,_168,_0.1)] text-[rgba(6,7,21,1)] dark:text-white"
                  >
                    {(g.image_vertical || g.image) && (
                      <img src={g.image_vertical || g.image} alt={g.title} className="h-10 w-auto rounded" />
                    )}
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate text-sm">{g.title}</span>
                      <span className="text-xs opacity-60 truncate">
                        {g.gameProvider?.title ?? g.provider} · position {currentPos}/{gameIds.length}
                      </span>
                    </div>
                    <input
                      type="number"
                      min={1}
                      max={gameIds.length}
                      value={inputValue}
                      onChange={(e) => setMovePositions((prev) => ({ ...prev, [g.identifier]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          submitMove(g)
                        }
                      }}
                      className="input input-bordered input-sm w-20 bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
                      title={`New position (1–${gameIds.length})`}
                    />
                    <button
                      type="button"
                      onClick={() => submitMove(g)}
                      className="btn btn-sm bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent"
                    >
                      Move
                    </button>
                  </div>
                )
              })}
            </>
          )}

          {allowAddRemove && notInSectionResults.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide bg-black/[0.04] dark:bg-white/[0.06] text-[rgba(6,7,21,0.7)] dark:text-white/70">
                Not in this section — add to it
              </div>
              {notInSectionResults.map((g) => (
                <button
                  type="button"
                  key={g.identifier}
                  onClick={() => submitAdd(g)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[rgba(132,_0,_168,_0.06)] dark:hover:bg-[rgba(132,_0,_168,_0.1)] text-[rgba(6,7,21,1)] dark:text-white"
                >
                  {(g.image_vertical || g.image) && <img src={g.image_vertical || g.image} alt={g.title} className="h-10 w-auto rounded" />}
                  <div className="flex flex-col min-w-0">
                    <span className="truncate text-sm">{g.title}</span>
                    <span className="text-xs opacity-60 truncate">{g.gameProvider?.title ?? g.provider}</span>
                  </div>
                </button>
              ))}
            </>
          )}

          {!allowAddRemove && !searching && inSectionResults.length === 0 && notInSectionResults.length > 0 && (
            <div className="px-3 py-2 text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">No matches in this section.</div>
          )}
        </div>
      )}
    </div>
  )
}
